import React, { useEffect, useRef } from "react";
import { useCallback } from "react";
import { useState } from "react";
import Loader from "components/Loader/Loader";
import ProviderCalendarView from "./ProviderCalenderView";
import { Appointment } from "types/appointments.js";
import Message from "../../../components/sharedComponents/reactHookComponents/UXComponents/Message";
import { useNavigate } from "react-router-dom";
import {
  checkIfItisToday,
  isIsoStringGreaterThan,
} from "../../../components/forms/providerAppointmentForms/appointments.helper";
import {
  ADMIN_APPT_TYPES_LABELS,
  CR_SYNC_AND_FAILURE_STATUSES,
  BCBA_SPECIALTY_ID,
} from "../../../constant/BiPortalConstants";
import { useLocation } from "react-router-dom";
import { ApiCalls } from "../../../components/forms/providerAppointmentForms/AllAppointmentsApi";
import { useWebSocket } from "utils/WebSockets/WebSocketProvider";
import CrBannerError from "../../../components/sharedComponents/Error/crBannerError";
import { useMsal } from "@azure/msal-react";
import { useSubmitApi } from "hooks/customHooks/authHook";
import { Button } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { isTablet, isDesktop } from "react-device-detect";
import { useAttestationStatus } from "hooks/customHooks/useAttestationStatus";
import { AttestationDateRange } from "types/attestation";
import {
  canScopeToPayPeriod,
  daysInCurrentPayPeriod,
} from "../../../components/forms/providerAppointmentForms/attestation.helper";
import AttestationBanner from "./AttestationBanner";
import AttestationCatchUp from "./AttestationCatchUp";
import moment from "moment";

interface LocationState {
  prefStartDate?: string;
  cancelledAppt?: number;
  hardCancellation?: number;
  /** Set when returning from the attest screen mid catch-up, so the run resumes. */
  attestationCatchUp?: boolean;
}

/**
 * Where an in-progress catch-up run is remembered.
 *
 * The run has to survive a round trip to another screen — entering mileage, editing an
 * appointment, attesting — but only the attest screen ever carried a flag back in router state.
 * Ten other screens navigate here with `prefStartDate` alone, so a run held in router state
 * ended the moment the BT did the very thing a blocked day asks of them: go and fix the mileage.
 *
 * Session-scoped on purpose. The run is an intent the BT expressed, not a record of anything —
 * it should die with the tab, and it must never outlive the browser session.
 */
const CATCH_UP_STORAGE_KEY = "attestationCatchUp";

/** The stored run, or null if there is none, the storage is unavailable, or it is unreadable. */
const readStoredCatchUp = (): { date?: string } | null => {
  try {
    const raw = sessionStorage.getItem(CATCH_UP_STORAGE_KEY);

    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private browsing, or a value written by an older build. Neither is worth failing over:
    // no stored run just means the BT starts one from the banner, as they always could.
    return null;
  }
};

const ProviderAppointmets: React.FC = () => {
  const { instance, inProgress } = useMsal();

  const activeAccount = instance.getActiveAccount();

  const { submitApiCall } = useSubmitApi();

  const navigate = useNavigate();
  const location = useLocation();
  const { prefStartDate } = location?.state || {} as LocationState;
  const { cancelledAppt } = location?.state || {} as LocationState;
  const { hardCancellation } = location?.state || {} as LocationState;
  const { attestationCatchUp } = location?.state || {} as LocationState;

  const { webSocketData } = useWebSocket();

  const [displayBanner, setDisplayBanner] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>();
  const [displayMileageError, setDisplayMileageError] =
    useState<boolean>(false);

  useEffect(() => {
    if (inProgress !== "none") {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [inProgress]);

  useEffect(() => {
    if (webSocketData?.athena || webSocketData?.cr) {
      if (webSocketData.athena !== true || webSocketData.cr !== true) {
        setDisplayBanner(true);
      } else {
        setDisplayBanner(false);
      }
    } else {
      setDisplayBanner(false);
    }
  }, [webSocketData]);

  useEffect(() => {
    // Reload the component when displayBanner changes
    setLoadingAppointments(true);
  }, [displayBanner]);

  const pollingTime = 15000;

  const user = activeAccount?.username;

  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const [pollCount, setPollCount] = useState<number>(0);

  const [freezePoll, setFreezPoll] = useState<boolean>(false);

  const [providerId, setProviderId] = useState<number>();
  const [providerName, setProviderName] = useState<string>();
  const [providerClinicState, setProviderClinicState] = useState<string>();
  const [providerClinicTimeZone, setProviderClinicTimeZone] = useState<string>();
  const [hasDurationMoreThan5Hours, setHasDurationMoreThan5Hours] =
    useState<boolean>(true);
  const [shouldAskRestBreakQuestion, setShouldAskRestBreakQuestion] =
    useState<boolean>(true);
  const [providerEmail, setProviderEmail] = useState<string>();

  const [providerClinicId, setProviderClinicId] = useState<number>();
  const [providerSpecialty, setProviderSpecialty] = useState<number>();

  // Fail closed: the feature stays hidden until the specialty is known to not be BCBA,
  // so a provider record with no specialty never falls through into showing it.
  const isAttestingProvider =
    providerSpecialty !== undefined && providerSpecialty !== BCBA_SPECIALTY_ID;

  /**
   * The window the day strip is showing, reported by the calendar.
   *
   * Status is fetched for this window as well as the pay period, which is what lets every day on
   * screen carry a marker. The current week always sits inside the current period — periods are
   * two whole Sunday-to-Saturday weeks — but stepping back lands on an earlier period, and
   * asking only about the current one left those weeks blank as though nothing were owed on
   * them.
   */
  const [visibleRange, setVisibleRange] = useState<AttestationDateRange>();

  // Stable, and compares before storing: this is called from the calendar's `datesSet`, and a
  // fresh object each time would refetch status on every re-render of the calendar.
  const handleVisibleRangeChange = useCallback((range: AttestationDateRange) => {
    setVisibleRange((previous) =>
      previous?.startDate === range.startDate &&
      previous?.endDate === range.endDate
        ? previous
        : range
    );
  }, []);

  const { attestationStatus, refresh: refreshAttestationStatus } =
    useAttestationStatus(
      isAttestingProvider ? providerId : undefined,
      visibleRange
    );

  const [attestationRequestedDate, setAttestationRequestedDate] =
    useState<string>();
  // Resumes automatically when the attest screen sends the BT back mid-run, or when any other
  // screen returns here — see CATCH_UP_STORAGE_KEY.
  const [inCatchUp, setInCatchUp] = useState<boolean>(
    () => Boolean(attestationCatchUp) || readStoredCatchUp() !== null
  );
  /**
   * The day the run is currently working through.
   *
   * Tracked separately from the day on screen so the BT can look at anything they like without
   * the run reacting. Advancing keys off *this* day being finished, not off whichever day
   * happens to be selected — otherwise navigating to a blocked day, a weekend, or a previous
   * pay period reads as "the day was attested, move along" and the calendar snaps back.
   *
   * Seeded from the day just attested when returning from the attest screen mid-run.
   */
  const [catchUpDate, setCatchUpDate] = useState<string | undefined>(() =>
    attestationCatchUp && prefStartDate
      ? moment(prefStartDate).format("YYYY-MM-DD")
      : readStoredCatchUp()?.date
  );

  /*
   * Keeps the stored run in step with the live one. A single sync point rather than a write at
   * every place the run moves, so a new transition cannot forget to record itself.
   */
  useEffect(() => {
    try {
      if (inCatchUp) {
        sessionStorage.setItem(
          CATCH_UP_STORAGE_KEY,
          JSON.stringify({ date: catchUpDate })
        );
      } else {
        sessionStorage.removeItem(CATCH_UP_STORAGE_KEY);
      }
    } catch {
      /*
       * Storage refused — private browsing, or a quota. The run still works on this screen; it
       * just will not survive leaving it, which is exactly how it behaved before. Not worth
       * interrupting the BT over.
       */
    }
  }, [inCatchUp, catchUpDate]);

  /*
   * Only the current pay period's days drive the count and the run. The status now also carries
   * days from whatever week is on screen, so reading `days` whole here would count a day left
   * owed by a closed period — or one the BT simply scrolled to — as work this period is asking
   * for, against a deadline that does not apply to it.
   *
   * Those days are not being hidden: they keep their marker on the strip, and the per-day checks
   * further down deliberately still read the whole list so opening one can still be confirmed.
   */
  const unattestedDays = daysInCurrentPayPeriod(attestationStatus).filter(
    (day) => !day.isAttested
  );
  /*
   * Every unattested day, including ones blocked on mileage. Blocked days used to be excluded so
   * the run would not strand the BT on a day they could not confirm — but a blocked day now
   * explains itself and can be fixed in place, so stepping over it just hides the days that need
   * the most work.
   */
  const catchUpRemainingDays = unattestedDays;

  const startCatchUp = (date: string) => {
    setInCatchUp(true);
    setCatchUpDate(date);
    setAttestationRequestedDate(date);
  };

  const exitCatchUp = () => {
    setInCatchUp(false);
    setCatchUpDate(undefined);
  };

  /** Days the BT chose to move past because their appointments would not load. */
  const [skippedDates, setSkippedDates] = useState<string[]>([]);
  /** The day the appointments currently in state belong to; see where it is set. */
  const [loadedDate, setLoadedDate] = useState<string>();

  /**
   * Opens a day that is held up by missing mileage, without starting a catch-up run.
   *
   * Reached only from the banner's mileage call to action, which appears when *every* remaining
   * day is blocked. There is no sequence to walk in that case — each day needs mileage entered
   * before it can be attested at all — so the BT is sent to the oldest one to deal with it, and
   * the banner re-offers whatever is left afterwards.
   */
  const goToMissingMileageDay = (date: string) => {
    setAttestationRequestedDate(date);
  };

  const [backendError, setBackendError] = useState<boolean>(false);

  const [disableConfirm, setDisableConfirm] = useState<boolean>(false);
  const [dayConfirmed, setDayConfirmed] = useState<boolean>(false);

  // A day just became confirmed, so the outstanding count is stale.
  useEffect(() => {
    if (dayConfirmed && isAttestingProvider) {
      refreshAttestationStatus();
    }
    // refreshAttestationStatus is stable for a given providerId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayConfirmed, isAttestingProvider]);

  const [loadingAppointments, setLoadingAppointments] =
    useState<boolean>(false);
  const [softloading, setsoftloading] = useState<boolean>(false);
  const [StartDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [displayConfirm, setConfirmDisplay] = useState<boolean>(false);
  const [responseData, setResponseData] = useState<any>();
  const [shouldPoll, setShouldPoll] = useState<boolean>(true);
  const [resyncCrId, setResyncCrId] = useState<number>();
  const [hasDriveTimeMileage, setHasDriveTimeMileage] = useState<boolean>(true);
  const [resyncDisable, setResyncDisable] = React.useState<boolean>(false);
  const [resyncCount, setResyncCount] = React.useState<number>(0);

  /**
   * Advances the catch-up run once the day it was working on has been attested.
   *
   * Keyed on `catchUpDate` — the run's own day — rather than on whatever is selected. Keying on
   * the selection cannot tell "this day was just attested" from "the BT navigated somewhere
   * else", so any click onto a blocked day, a day off, or a previous pay period was read as
   * completion and yanked the calendar back to the run.
   *
   * Requires a *positive* confirmation that the day is attested, rather than inferring it from
   * the day no longer being owed. Those are not the same thing: the owed list is rebuilt from
   * the schedule, and a day only earns a place on it while it holds paid work. So when a
   * scheduler cancels appointments in Axon — which deletes them outright rather than marking
   * them cancelled — the day can leave the list with the BT having done nothing at all.
   * Treating that as completion ticked the day off and jumped the calendar forward, which is
   * exactly what QA hit on a day that was still perfectly attestable.
   */
  useEffect(() => {
    if (!inCatchUp || !catchUpDate || attestationRequestedDate) {
      return;
    }
    const runDayIsAttested = (attestationStatus?.days ?? []).some(
      (day) => day.date === catchUpDate && day.isAttested
    );

    if (!runDayIsAttested) {
      return;
    }
    // Days the BT chose to move past are not offered again in this run, or the run would land
    // back on one they already declined and loop.
    const nextDay = catchUpRemainingDays.find(
      (day) => !skippedDates.includes(day.date)
    );

    if (nextDay) {
      setCatchUpDate(nextDay.date);
      setAttestationRequestedDate(nextDay.date);
    }
    // The days list is derived fresh each render; keying on a string of each day's date *and*
    // attested flag keeps this effect stable while still reacting to a refreshed status. The
    // flag has to be in the key — advancing now turns on it, and a day flipping to attested
    // does not change the set of dates the status reports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inCatchUp,
    catchUpDate,
    attestationRequestedDate,
    (attestationStatus?.days ?? [])
      .map((day) => `${day.date}:${day.isAttested ? 1 : 0}`)
      .join(","),
    skippedDates,
  ]);

  useEffect(() => {
    if (appointments.length === 0 || freezePoll || backendError) {
      setConfirmDisplay(false);
    } else {
      setConfirmDisplay(true);
    }
  });

  useEffect(() => {
    const hasDriveTimeAppts = appointments.some(
      (appointment) =>
        appointment.title === ADMIN_APPT_TYPES_LABELS.driveTime ||
        appointment.title === ADMIN_APPT_TYPES_LABELS.mileageOnly
    );
    if (hasDriveTimeAppts) {
      const hasDriveTimeMileage = appointments.every((appointment) => {
        if (
          appointment.title === ADMIN_APPT_TYPES_LABELS.driveTime ||
          appointment.title === ADMIN_APPT_TYPES_LABELS.mileageOnly
        ) {
          return (
            appointment.driveTimeMileage !== null &&
            appointment.reimbursableMileage !== null
          );
        }
        // Non-Drive Time appointments
        return true;
      });

      setHasDriveTimeMileage(hasDriveTimeMileage);
    }

    setPollCount(pollCount + 1);
  }, [appointments]);

  /**
   * Read straight off the loaded day rather than reusing `hasDriveTimeMileage`, which is state
   * written by an effect and so is a render behind, and which is only recomputed on days that
   * actually carry a Drive Time appointment.
   */
  const dayIsMissingMileage = appointments.some(
    (appointment) =>
      (appointment.title === ADMIN_APPT_TYPES_LABELS.driveTime ||
        appointment.title === ADMIN_APPT_TYPES_LABELS.mileageOnly) &&
      (appointment.driveTimeMileage === null ||
        appointment.reimbursableMileage === null)
  );

  /**
   * Whether the server says the day on screen cannot be attested yet, for any reason.
   *
   * The server is the authority here, not this screen's own mileage check. Those two can
   * disagree — they read the appointment title from different places — and they will diverge
   * outright the moment a blocking reason exists that this build predates. Deferring to the
   * server means a day it refuses can never be attested from here, whatever the reason is.
   *
   * Absence of an answer is not a block: when the status call fails there is no `days` list, and
   * the schedule has to stay usable.
   */
  const attestationBlocksThisDay =
    isAttestingProvider &&
    !!StartDate &&
    (attestationStatus?.days ?? []).some(
      (day) =>
        day.date === moment(StartDate).format("YYYY-MM-DD") &&
        !day.isAttested &&
        day.isBlocked
    );

  /**
   * States why the day on screen cannot be attested, without making the BT press Confirm and be
   * refused to find out.
   *
   * Shown for any day the server is holding up on mileage, not only one reached from the
   * banner's call to action. A BT who clicks the day in the strip has exactly the same problem,
   * and previously got a disabled button with no explanation anywhere on the page.
   *
   * Gated on the *server* calling the day blocked, not merely on this screen spotting an empty
   * mileage field. Confirm is disabled on plenty of days where mileage is beside the point — a
   * future day, and today until the last paid appointment has run — and a recurring drive under
   * 15 miles stores a null reimbursable mileage on every instance, including those. Without this
   * the BT is told to add mileage to confirm a day that cannot be confirmed at all yet, and
   * adding it changes nothing.
   *
   * Derived rather than stored: `getAppointments` clears `displayMileageError` on every poll, so
   * a stored flag would be wiped a few seconds after it appeared. This also clears itself once
   * the mileage is entered.
   *
   * Not shown once the day is confirmed: the message is about what has to happen *before*
   * confirming, so on an already-attested day it asks for something there is no longer a reason
   * to do. The appointment keeps its red stripe — the mileage really is missing — but the day is
   * no longer waiting on it.
   */
  const dayIsBlockedOnMileage =
    attestationBlocksThisDay &&
    dayIsMissingMileage &&
    !dayConfirmed &&
    /*
     * Only once the list in state is the selected day's. `dayIsMissingMileage` reads
     * `appointments`, which still holds the previous day's for as long as the fetch takes — so
     * without this, moving off a day with missing mileage carries its red notice onto the next
     * day and shows it over that day's skeletons. The old predicate happened to avoid this by
     * comparing against the day the banner had sent the BT to; widening it to every day removed
     * that accident, so the check has to be explicit.
     */
    loadedDate === moment(StartDate).format("YYYY-MM-DD");

  /**
   * The server reports the day on screen as owed and ready to attest. Used to override this
   * screen's own last-appointment check, which cannot tell paid work from an unpaid block.
   */
  const attestationAllowsThisDay =
    isAttestingProvider &&
    !!StartDate &&
    (attestationStatus?.days ?? []).some(
      (day) =>
        day.date === moment(StartDate).format("YYYY-MM-DD") &&
        !day.isAttested &&
        !day.isBlocked
    );

  /**
   * The day on screen is owed but nothing came back for it, so there is nothing to review and
   * the run cannot advance off it — advancing requires the day to stop being outstanding.
   *
   * Keyed off the contradiction rather than off an empty list: a day with genuinely no work is
   * never reported as owed, so an ordinary non-working day never reaches this.
   */
  const catchUpDayIsUnloadable =
    inCatchUp &&
    !!StartDate &&
    !loadingAppointments &&
    !backendError &&
    appointments.length === 0 &&
    /*
     * Only judge a day once the list in state is actually that day's. Selecting a day moves
     * `StartDate` immediately while `appointments` still holds the previous day's — briefly
     * empty and not yet loading — which made this notice flash on every navigation and read as
     * though something had blocked the click.
     */
    loadedDate === moment(StartDate).format("YYYY-MM-DD") &&
    catchUpRemainingDays.some(
      (day) => day.date === moment(StartDate).format("YYYY-MM-DD")
    );

  /**
   * Moves the run to the next owed day, leaving this one outstanding so it is still reported.
   * Ends the run when there is nothing else to go to, which hands the BT back to the banner
   * rather than a completion message that would not be true.
   */
  const skipCurrentDay = () => {
    const currentDate = StartDate
      ? moment(StartDate).format("YYYY-MM-DD")
      : undefined;
    const nextDay = catchUpRemainingDays.find(
      (day) =>
        day.date !== currentDate &&
        !skippedDates.includes(day.date)
    );

    setSkippedDates((previous) =>
      currentDate && !previous.includes(currentDate)
        ? [...previous, currentDate]
        : previous
    );

    if (nextDay) {
      setCatchUpDate(nextDay.date);
      setAttestationRequestedDate(nextDay.date);
      return;
    }
    exitCatchUp();
  };

  /**
   * Days already re-checked after their mileage appeared. Bounds the effect below to one
   * refetch per day: if the backend still considers the day blocked, retrying would loop.
   */
  const mileageRecheckedDatesRef = useRef<Set<string>>(new Set());

  /**
   * Identity of the loaded day's appointments as attestation sees them — which appointments
   * exist and what mileage they carry. Anything else about them cannot change whether the day
   * is owed or blocked.
   */
  const appointmentsFingerprint = appointments
    .map(
      (appointment) =>
        `${appointment.id}:${appointment.driveTimeMileage ?? ""}:${
          appointment.reimbursableMileage ?? ""
        }`
    )
    .join("|");

  const lastFingerprintRef = useRef<{ date?: string; fingerprint?: string }>(
    {}
  );

  /**
   * Keeps attestation status in step with the schedule.
   *
   * The schedule re-polls every 15 seconds; status is otherwise only fetched on mount and after
   * a confirm. So mileage added elsewhere, or an appointment added in Axon, would show in the
   * list while the banner, the markers and the Confirm button carried on from a status fetched
   * minutes earlier — letting a day that had become blocked still be attested.
   *
   * Only refetches when the *same* day's appointments actually change. A plain day change does
   * not qualify, or every click would cost an extra call.
   */
  useEffect(() => {
    if (!isAttestingProvider || !loadedDate || loadingAppointments) {
      return;
    }
    const previous = lastFingerprintRef.current;
    const sameDayContentChanged =
      previous.date === loadedDate &&
      previous.fingerprint !== appointmentsFingerprint;

    lastFingerprintRef.current = {
      date: loadedDate,
      fingerprint: appointmentsFingerprint,
    };

    if (sameDayContentChanged) {
      refreshAttestationStatus();
    }
    // refreshAttestationStatus is stable for a given providerId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAttestingProvider,
    loadedDate,
    loadingAppointments,
    appointmentsFingerprint,
  ]);

  /** Days already re-checked after coming back empty, so the refetch below runs once each. */
  const emptyDayRecheckedDatesRef = useRef<Set<string>>(new Set());

  /**
   * An owed day with no appointments usually means the status is simply out of date — the work
   * was removed after it was fetched. Re-checking once resolves that case silently: the day
   * drops out of the list and the run advances on its own, so the notice never appears.
   */
  useEffect(() => {
    if (!catchUpDayIsUnloadable || !StartDate) {
      return;
    }
    const selectedDate = moment(StartDate).format("YYYY-MM-DD");

    if (emptyDayRecheckedDatesRef.current.has(selectedDate)) {
      return;
    }
    emptyDayRecheckedDatesRef.current.add(selectedDate);
    refreshAttestationStatus();
    // refreshAttestationStatus is stable for a given providerId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchUpDayIsUnloadable, StartDate]);

  /**
   * Picks up mileage the BT just entered. The status that marked the day blocked is stale the
   * moment it is saved, and nothing else refetches it — the only other refresh is on confirm.
   * Without this the banner keeps telling the BT to add mileage they have already added, with
   * no attest button in sight, until they reload the page.
   */
  useEffect(() => {
    if (!isAttestingProvider || !StartDate || loadingAppointments) {
      return;
    }
    const selectedDate = moment(StartDate).format("YYYY-MM-DD");
    const statusSaysBlocked = (attestationStatus?.days ?? []).some(
      (day) => day.date === selectedDate && day.isBlocked
    );

    if (
      !statusSaysBlocked ||
      dayIsMissingMileage ||
      mileageRecheckedDatesRef.current.has(selectedDate)
    ) {
      return;
    }
    mileageRecheckedDatesRef.current.add(selectedDate);
    refreshAttestationStatus();
    // refreshAttestationStatus is stable for a given providerId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAttestingProvider,
    StartDate,
    loadingAppointments,
    dayIsMissingMileage,
    attestationStatus,
  ]);

  const pollData = (poll: boolean, start: any, end: any, load: boolean) => {
    if (poll) {
      getAppointments(start, end, load);
    } else {
      return;
    }
  };

  useEffect(() => {
    if (backendError) {
      return;
    } else if (pollCount > 125) {
      //keep 123 (30 min)
      setFreezPoll(true);
      return;
    } else {
      if (shouldPoll === true) {
        const interval = setInterval(() => {
          pollData(true, StartDate, endDate, false);
        }, pollingTime);

        return () => clearInterval(interval);
      } else return;
    }
  }, [backendError, StartDate, endDate, pollCount, shouldPoll]);

  const getLatestDate = (appointments: Appointment[]): Date | null => {
    if (appointments.length === 0) {
      return null;
    }
    const dateObjects = appointments.map(
      (appointment) => new Date(appointment.start)
    );

    const latestDate = new Date(
      Math.max(...dateObjects.map((date) => date.getTime()))
    );

    return latestDate;
  };

  useEffect(() => {
    /*
     * Kept from AXON-3550 rather than removed: gating on `crSyncEnabled` already stops stale
     * crStatus values from blocking attestation while sync is off, and preserves the safeguard
     * for if it is ever turned back on.
     */
    let areAppointmentsImpure = false;

    appointments.map((appointment) => {
      if (
        appointment.crSyncEnabled === true &&
        appointment.crStatus &&
        CR_SYNC_AND_FAILURE_STATUSES.includes(appointment.crStatus)
      ) {
        areAppointmentsImpure = true;
      }
    });

    const lastApptTime = getLatestDate(appointments);

    let currentTimeGreaterThanLastApptTime = false;

    if (lastApptTime) {
      currentTimeGreaterThanLastApptTime = isIsoStringGreaterThan(
        new Date(),
        lastApptTime!
      )
        ? false
        : true;
    }

    /*
     * `currentTimeGreaterThanLastApptTime` is measured over every appointment on the day,
     * including unpaid ones, so a trailing Unavailable or PTO block keeps it true long after the
     * BT stopped working. The server applies the same rule correctly — last *paid* appointment —
     * and paycodes are not on the wire here, so when it reports the day as owed and unblocked,
     * that answer wins. Without this the banner would count a day the Confirm button refuses,
     * with nothing on screen to move the BT forward.
     */
    if (StartDate && checkIfItisToday(StartDate)) {
      setDisableConfirm(true);
    } else if (dayConfirmed) {
      setDisableConfirm(true);
      /*
       * Unconditional, unlike the last-appointment check below: a genuine sync failure while CR
       * sync is enabled is a data-integrity reason not to attest, so the server reporting the day
       * as owed must not override it.
       */
    } else if (areAppointmentsImpure) {
      setDisableConfirm(true);
    } else if (attestationBlocksThisDay) {
      setDisableConfirm(true);
    } else if (currentTimeGreaterThanLastApptTime && !attestationAllowsThisDay) {
      setDisableConfirm(true);
    } else {
      setDisableConfirm(false);
    }
  }, [
    attestationAllowsThisDay,
    StartDate,
    appointments,
    dayConfirmed,
    loadingAppointments,
    hasDriveTimeMileage,
    attestationBlocksThisDay,
  ]);

  const filteredResyncAppointments: any = appointments.filter(
    (appointment) => appointment.id === resyncCrId
  );

  async function getAppointments(
    startDate: any,
    endDate: any,
    setLoading: boolean
  ) {
    setStartDate(startDate);
    setEndDate(endDate);
    setBackendError(false);
    setDisplayMileageError(false);

    const payload = {
      user_id: 306,
      isProvider: true,
      startDate: startDate,
      endDate: endDate,
      email: activeAccount?.username,
      providerName: encodeURIComponent(activeAccount?.name!),
    };
    setsoftloading(setLoading);
    setLoadingAppointments(true);

    try {
      const response = await submitApiCall(
        ApiCalls.ProviderAppointments,
        payload
      );
      setResponseData(response);
      const results: any = [];
      results.push(
        ...response.data[0].appointments.map((appointment: Appointment) => {
          console.log(appointment, "Vedavyas appointment");
          return {
            ...appointment,
            title: appointment.type_name,
            start: appointment.startAt,
            end: appointment.endAt,
            client: appointment.client_name,
            provider: appointment.provider_name,
            isCancelling:
              cancelledAppt && cancelledAppt === appointment.id ? true : false,
            isHardCancelling:
              hardCancellation && hardCancellation === appointment.id
                ? true
                : false,
          };
        })
      );

      results.sort((a: any, b: any) => {
        return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
      });

      setProviderId(response.data[0].providerId);
      setProviderName(response.data[0].providerName);
      setProviderClinicId(response.data[0].providerClinicId);
      setProviderClinicState(response.data[0].providerClinicState);
      setProviderClinicTimeZone(response.data[0].providerClinicTimeZone);
      setHasDurationMoreThan5Hours(response.data[0].hasDurationMoreThan5Hours);
      setShouldAskRestBreakQuestion(
        response.data[0].shouldAskRestBreakQuestion
      );
      setProviderEmail(response.data[0].providerEmail);
      const specialtyId = response.data[0].providerSpecialty?.id;
      setProviderSpecialty(specialtyId);
      
      if (response.data[0].isConfirmed) {
        setDayConfirmed(response.data[0].isConfirmed);
      } else {
        setDayConfirmed(false);
      }
      setAppointments(results);
      // Which day the list in state actually describes. `StartDate` moves the moment a day is
      // selected, so without this the two disagree for as long as the fetch takes.
      setLoadedDate(moment(startDate).format("YYYY-MM-DD"));
      setBackendError(false);
      setLoadingAppointments(false);
      setsoftloading(false);
    } catch (error) {
      setBackendError(true);
      setLoadingAppointments(false);
      setsoftloading(false);
    }
  }

  const handleDataChange = useCallback(
    (startStr: any, endStr: any) => {
      setHasDriveTimeMileage(true);
      getAppointments(startStr, endStr, true);
      setPollCount(0);
      navigate(location.pathname, { state: {} });
    },
    [getAppointments, navigate]
  );

  useEffect(() => {
    setLoadingAppointments(true);
  }, []);

  if (loading || !user) {
    return <Loader />;
  }

  const reloadAppointments = () => {
    handleDataChange(StartDate, endDate);
  };


  const getAppointmentsTable = (
    appointments: Appointment[],
    providerId: number,
    providerName: string,
    providerClinicId: number,
    providerClinicTimeZone: string
  ) => {
    return (
      <ProviderCalendarView
        appointments={appointments}
        handleDataChange={handleDataChange}
        softloading={softloading}
        providerId={providerId}
        providerName={providerName}
        providerClinicId={providerClinicId}
        providerClinicTimeZone={providerClinicTimeZone}
        providerEmail={user}
        disableAddAppt={dayConfirmed}
        dayConfirmed={dayConfirmed}
        toDate={responseData?.startDate}
        prefDate={prefStartDate}
        attestationDays={
          isAttestingProvider ? attestationStatus?.days ?? [] : []
        }
        attestationSlot={renderAttestation()}
        onVisibleRangeChange={handleVisibleRangeChange}
        hideAvailabilityNotice={isAttestingProvider && inCatchUp}
        requestedDate={attestationRequestedDate}
        onRequestedDateHandled={() => setAttestationRequestedDate(undefined)}
        getAppointments={getAppointments}
        startDateProps={StartDate}
        endDateProps={endDate}
        setShouldPoll={setShouldPoll}
        setResyncCrId={setResyncCrId}
        filteredResyncAppointments={filteredResyncAppointments}
        resyncDisable={resyncDisable}
        setResyncDisable={setResyncDisable}
        resyncCount={resyncCount}
        setResyncCount={setResyncCount}
        displayMileageError={displayMileageError || dayIsBlockedOnMileage}
        backendError={backendError}
        reloadAppointments={reloadAppointments}
        onCloseError={() => setBackendError(false)}
      />
    );
  };

  /**
   * Attestation surface for the schedule screen. Handed to the calendar view rather than
   * rendered here so it sits inside the page content, aligned with the availability notice,
   * instead of floating above the heading at its own width.
   */
  const renderAttestation = () => {
    if (!isAttestingProvider) {
      return null;
    }
    /*
     * With no status loaded there are no known outstanding days, and the catch-up view reads an
     * empty list as "finished" — so it would announce that every day in the pay period is
     * attested on the strength of a failed request. Silence is the only honest option.
     */
    if (inCatchUp && !attestationStatus) {
      return null;
    }
    /*
     * Same reasoning for a status whose pay period cannot be read: the run's days are scoped to
     * that window, so without it the remaining list is empty for want of a boundary rather than
     * because the BT is finished — and the catch-up view would read that as an all-clear.
     */
    if (inCatchUp && !canScopeToPayPeriod(attestationStatus)) {
      return null;
    }

    return inCatchUp ? (
      <AttestationCatchUp
        remainingDays={catchUpRemainingDays}
        hasUnloadableDay={catchUpDayIsUnloadable}
        onSkipDay={skipCurrentDay}
        onExit={exitCatchUp}
      />
    ) : (
      <AttestationBanner
        attestationStatus={attestationStatus}
        onReviewAndAttest={startCatchUp}
        onAddMileage={goToMissingMileageDay}
      />
    );
  };

  const isProviderDataAvailable = user ? true : false;

  const confirmSchedule = () => {
    if (!hasDriveTimeMileage) {
      setTimeout(() => {
        setDisplayMileageError(true);
      }, 0); // Show mileage error message if drive time mileage is missing

      return; // Return early to prevent further execution
    }
    navigate("/confirm-schedule",{
      state: {
        providerId: providerId,
        providerName: providerName,
        date: StartDate,
        providerEmail: providerEmail,
        providerClinicState: providerClinicState,
        providerClinicTimeZone: providerClinicTimeZone,
        hasDurationMoreThan5Hours: hasDurationMoreThan5Hours,
        shouldAskRestBreakQuestion: shouldAskRestBreakQuestion,
        attestationCatchUp: inCatchUp,
      },
    });
  };

  return (
    <>
      <div className="primary-wrapper fd-column">
        <div className= {isTablet || isDesktop ? "primary-wrapper-card" : "primary-wrapper-card-mobile"}>
        {displayBanner && (
          <div>
            <CrBannerError></CrBannerError>
          </div>
        )}
        {freezePoll ? (
          <>
            <Message setPollCount={setPollCount} setBoolean={setFreezPoll} />
          </>
        ) : (
          isProviderDataAvailable &&
          getAppointmentsTable(
            appointments,
            providerId!,
            providerName!,
            providerClinicId!,
            providerClinicTimeZone!
          )
        )}
        {displayConfirm && providerSpecialty !== BCBA_SPECIALTY_ID && (
          <div className="confirm-day-wrapper h-center">
            {!softloading && (
              <div className="confirm-schedule-button">
                <Button variant="contained"
                  disabled={disableConfirm}
                  onClick={confirmSchedule}
                  endIcon={<CheckCircleOutlineIcon />}>
                  Confirm
                </Button>
                {/*
                  * Names the day being confirmed — "Confirm" alone leaves it ambiguous whether
                  * the BT is signing off one day or the whole pay period — and, when the button
                  * is disabled for missing mileage, says so here. The mileage notice sits several
                  * hundred pixels further up past the appointment cards, so on its own it does
                  * not explain the grey button next to it.
                  *
                  * Only claims mileage when mileage is genuinely the blocker; the button is also
                  * disabled for a future day, an already-confirmed day and a sync failure.
                  */}
                {StartDate && (
                  <div
                    className={`confirm-schedule-scope${
                      dayIsBlockedOnMileage
                        ? " confirm-schedule-scope--blocked"
                        : ""
                    }`}
                    data-testid="confirm-scope"
                  >
                    {/*
                      * An attested day is checked first. It is disabled *because* it is done,
                      * and it can still carry missing mileage — so testing the mileage first
                      * told BTs to add mileage in order to confirm a day already confirmed.
                      */}
                    {dayConfirmed
                      ? `You have already confirmed ${moment(StartDate).format(
                          "dddd, MMMM D"
                        )}`
                      : dayIsBlockedOnMileage
                      ? `Add the missing mileage to confirm ${moment(
                          StartDate
                        ).format("dddd, MMMM D")}`
                      : `You are confirming your schedule and mileage for ${moment(
                          StartDate
                        ).format("dddd, MMMM D")}`}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* <Footer /> */}
        </div>
      </div>
    </>
  );
};

export default React.memo(ProviderAppointmets);
